import { calendarMonthsBetween } from "./calculation-dates.js";
import { additionalExpenses } from "./calculation-expenses.js";
import type { AdditionalExpense, CalculatorInput, CalculatorResult } from "../server/types.js";

export interface ClientCalculationSnapshot {
  objectName: string;
  objectAddress: string;
  preparedBy: string;
  createdAt: string;
  input: CalculatorInput;
  result: CalculatorResult;
}
export interface ReportSection {
  title: string;
  rows: Array<[string, string]>;
  note?: string;
}
const rubles = new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB", maximumFractionDigits: 2 });
const percentage = new Intl.NumberFormat("ru-RU", { style: "percent", maximumFractionDigits: 1 });
const number = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 6 });
const date = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Moscow" });
export const money = (value: number | undefined) => rubles.format(value ?? 0);
export function displayDate(value: string | null | undefined) {
  const parsed = new Date(value || "");
  return Number.isFinite(parsed.getTime()) ? date.format(parsed) : "Не указана";
}
export function buildCalculationReport({ input: i, result: r }: ClientCalculationSnapshot) {
  const expenseCaption = { "outside-price": "сверх цены", "price-outside-ddu": "в цене, вне ДДУ", "inside-ddu": "уже в ДДУ" };
  const expenseRows = (phase: AdditionalExpense["phase"], months: number): Array<[string,string]> => additionalExpenses(i).filter(e=>e.phase===phase && e.amount>0).map(e=>[`${e.name}${phase === "after-rent" ? "" : ` (${expenseCaption[e.treatment]})`}`, money(e.amount*(e.monthly ? months : 1))]);
  const afterRentMonths = r.afterRentExpenseMonths ?? calendarMonthsBetween(r.rentStartDate ? new Date(r.rentStartDate + "T12:00:00Z") : null, r.saleDate ? new Date(r.saleDate + "T12:00:00Z") : null);
  const pct = (value: number | undefined) => `${number.format(value ?? 0)}%`;
  const duration = (value: number) => `${number.format(value)} ${{one:"год",few:"года",many:"лет",other:"года",two:"года",zero:"лет"}[new Intl.PluralRules("ru").select(value)]}`;
  const years = r.paybackYears || 0;
  const yearsWord = years % 100 >= 11 && years % 100 <= 14 ? "лет" : years % 10 === 1 ? "год" : years % 10 >= 2 && years % 10 <= 4 ? "года" : "лет";
  const cashflowRows: Array<[string, string]> = r.actualRentMonths > 0 ? [
    [r.averageMonthlyCashflow < 0 ? "В среднем нужно доплачивать" : "Средний остаток собственнику", `${money(Math.abs(r.averageMonthlyCashflow))}/мес.`]
  ] : [["Остаток после платежей в месяц", "Аренда не запланирована"]];
  const sections: ReportSection[] = [
    { title: "1. Ключевые даты", rows: [
      ["Дата покупки", displayDate(r.purchaseDate)],
      ["Сдача дома", displayDate(r.projectDate)],
      ["Получение ключей", displayDate(r.keysDate)],
      ["Срок ремонта", `${number.format(i.repairMonths)} мес.`],
      ["Начало сдачи квартиры", displayDate(r.rentStartDate)],
      ["Месяцев от покупки до сдачи", String(r.monthsToRent)],
      ["Расчётная дата продажи", displayDate(r.saleDate)]
    ] },
    { title: "2. Ипотека", rows: [
      ["Полная стоимость квартиры после скидки", money(i.price)],
      ["Площадь квартиры", `${number.format(i.area)} м²`],
      ["Сумма ДДУ", money(r.dduAmount ?? i.price - (i.bookingCost || 0))],
      ["Первоначальный взнос", money(i.downPayment)],
      ["Бронь (уменьшает сумму кредита)", money(i.bookingCost)],
      ["Другие платежи в цене вне ДДУ при покупке", money(r.outsideDduAtPurchase)],
      ["Отложенные платежи в цене вне ДДУ", money(r.outsideDduDeferred)],
      ["Сумма кредита", money(r.loanAmount)],
      ["Ставка", `${pct(i.rate)} годовых`],
      ["Срок кредита", duration(i.loanYears)],
      [i.manualPayment > 0 ? "Ежемесячный платёж (вручную)" : "Ежемесячный платёж", money(r.monthlyPayment)],
      ["Платежи до сдачи", money(r.mortgageBeforeRent)],
      ["Платежи после сдачи до продажи", money(r.mortgageAfterRent)],
      ["Всего ипотечных платежей", money(r.mortgageTotal)],
      ["Остаток ипотечного долга при продаже", money(r.remainingDebt)]
    ] },
    { title: "3а. Расходы при покупке", rows: [
      ["Первоначальный взнос", money(i.downPayment)],
      ["Бронь (сверх первоначального взноса)", money(i.bookingCost)],
      ["Расходы по оформлению", money(i.registrationCost)],
      ["Первый платёж страхования жизни", money(r.insuranceLifeAtPurchase)],
      ...expenseRows("purchase",1),
      ["Итого при покупке", money(r.purchaseCosts)]
    ] },
    { title: "3б. Расходы после покупки до сдачи", rows: [
      ["Ипотечные платежи", money(r.mortgageBeforeRent)],
      [i.repairIncludes ? "Ремонт с мебелью и техникой" : "Ремонт", money(i.repairCost)],
      ...(!i.repairIncludes ? [["Мебель", money(i.furnitureCost)], ["Бытовая техника", money(i.applianceCost)]] as Array<[string,string]> : []),
      ["Ремонт, мебель и техника — всего", money(r.fitout)],
      ...(i.insuranceLifeAnnual ? [["Страхование жизни за год", money(i.insuranceLifeAnnual)], ["Срок страхования жизни", duration(i.insuranceLifeYears)]] as Array<[string,string]> : []),
      ...(i.insurancePropertyAnnual ? [["Страхование имущества за год", money(i.insurancePropertyAnnual)], ["Срок страхования имущества", duration(i.insurancePropertyYears)]] as Array<[string,string]> : []),
      ["Страхование жизни (кроме первого платежа)", money(r.insuranceLifeBeforeRent)],
      ["Страхование имущества", money(r.insurancePropertyBeforeRent)],
      ["Коммунальные без счётчиков", money(r.utilitiesBeforeRent)],
      ["Оценка при оформлении собственности", money(i.appraisalCost)],
      ["Приёмка квартиры", money(i.acceptanceCost)],
      ["Налог на имущество", money(r.propertyTaxBeforeRent)],
      ["Госпошлина", money(i.dutyCost)],
      ...expenseRows("before-rent",r.monthsToRent),
      ["Итого после покупки до сдачи", money(r.afterPurchaseCosts)],
      ["Всего собственных вложений до сдачи", money(r.preRentTotal)],
      ["Собственные вложения на м²", money(r.sqmCost)]
    ] },
    { title: "4. Аренда", rows: [
      ["Ставка в первый год", `${money(i.rentRate)}/мес.`],
      ["Ежегодная индексация", pct(i.rentIndex)],
      ["Планируемый срок аренды", `${number.format(Math.round(i.plannedRentYears * 12))} мес.`],
      ["Фактический срок в итогах", `${r.actualRentMonths} мес.`],
      ["Валовый доход с индексацией", money(r.rentGross)]
    ] },
    { title: "5. Расходы после сдачи", rows: [
      ["Ипотечные платежи", money(r.mortgageAfterRent)],
      ["Страхование жизни", money(r.insuranceLifeAfterRent)],
      ["Страхование имущества", money(r.insurancePropertyAfterRent)],

      [`Налог со сдачи (${pct(i.rentTaxPct)})`, money(r.rentTax)],
      ["Налог на имущество", money(r.propertyTaxTotal - r.propertyTaxBeforeRent)],
      ["Коммунальные за месяц", money(i.utilities)],
      ["Рост коммунальных в год", pct(i.utilitiesGrowthPct)],
      ["Коммунальные во время аренды платит", i.ownerPaysUtilities ? "Собственник" : "Арендатор"],
      ["Налог на имущество за год", money(i.propertyTaxAnnual)],
      ["Коммунальные без счётчиков", money(r.utilitiesDuringRent)],
      [`Управляющая компания (${pct(i.managementPct)})`, money(r.management)],
      ...(afterRentMonths > 0 ? expenseRows("after-rent",afterRentMonths) : []),
      ["Итого расходов после сдачи", money(r.afterRentCosts)],
      ...cashflowRows
    ] },
    { title: "6. Продажа", rows: [
      ["Рост стоимости в год", pct(i.valueIndex)],
      ["Срок до продажи", duration(i.ownershipYears || i.investmentYears)],
      ["Расчётная стоимость продажи", money(r.salePrice)],
      [`Услуги риелтора при продаже (${pct(i.realtorPct)})`, money(r.realtor)],
      ["Ставка налога с продажи", pct(i.saleTaxPct)],
      ["Владение без налога", duration(i.minOwnYears)],
      ["Налог с продажи", money(r.saleTax)],
      ["Остаток ипотечного долга при продаже", money(r.remainingDebt)],
      ["Чистые деньги после продажи", money(r.netSale)]
    ] },
    { title: "7. Итоги и окупаемость", rows: [
      ["Расходы без погашения остатка долга", money(r.allOutflows)],
      ["Доходы: аренда и продажа", money(r.allInflows)],
      ["Погашение остатка ипотечного долга при продаже", money(r.remainingDebt)],
      ["Чистая прибыль", money(r.profit)],
      ["Доходность вложений", percentage.format(r.roi)],
      ["Горизонт доходности", duration(i.investmentYears || i.ownershipYears)],
      ["Доходность в год", percentage.format(r.annualRoi)],
      ["Окупаемость", r.paybackReached ? `${years} ${yearsWord} ${r.paybackMonthsRemainder} мес.` : `Не достигнута за ${r.paybackHorizonYears} лет`],
      ["Расчётная дата окупаемости", r.paybackReached ? displayDate(r.paybackDate) : "Не достигнута"]
    ] },
  ];
  return {
    kpis: [
      ["Собственные вложения до сдачи", money(r.preRentTotal)],
      ["Платёж в месяц", money(r.monthlyPayment)],
      ["Чистая прибыль", money(r.profit)]
    ] as Array<[string, string]>,
    sections
  };
}

export function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]!));
}
export function reportSectionsHtml(sections: ReportSection[]) {
  return sections.map((section) => `<section class="report-section"><h2>${escapeHtml(section.title)}</h2><dl>${section.rows.map(([label, value]) => `<div class="report-row"><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`).join("")}</dl>${section.note ? `<p class="report-note">${escapeHtml(section.note)}</p>` : ""}</section>`).join("");
}
